import React, { useEffect, useState } from 'react';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const certificateTypeLabels = {
  reposo: 'Reposo',
  asistencia: 'Asistencia',
  condicion: 'Condicion medica',
};

const MedicalCertificatesPanel = ({
  appointmentId,
  patient,
  doctor,
  user,
  defaultDiagnosis = '',
}) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [certificateType, setCertificateType] = useState('asistencia');
  const [diagnosis, setDiagnosis] = useState(defaultDiagnosis);
  const [restDays, setRestDays] = useState('');
  const [statement, setStatement] = useState('');
  const [observations, setObservations] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDiagnosis((current) => current || defaultDiagnosis);
  }, [defaultDiagnosis]);

  useEffect(() => {
    if (!appointmentId) return;
    auditFetch(`${backendUrl}/api/medical-certificates/appointment/${appointmentId}`, {}, user)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('No se pudieron cargar certificados'))))
      .then((data) => setCertificates(Array.isArray(data.certificates) ? data.certificates : []))
      .catch((error) => {
        console.error('Error loading medical certificates:', error);
        setStatusMessage('No se pudieron cargar certificados previos.');
      });
  }, [appointmentId, backendUrl, user]);

  const openPdf = (certificate) => {
    const url = `${backendUrl}/api/medical-certificates/${certificate.id}/pdf`;
    fetch(url, { headers: buildAuditHeaders(user) })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo generar el PDF');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => {
        console.error('Error opening certificate PDF:', error);
        setStatusMessage('No se pudo abrir el PDF del certificado.');
      });
  };

  const saveCertificate = async () => {
    if (certificateType === 'reposo' && (!restDays || Number(restDays) <= 0)) {
      setStatusMessage('Para reposo, indica al menos 1 dia.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    try {
      const response = await auditFetch(`${backendUrl}/api/medical-certificates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          patient,
          doctor,
          certificateType,
          diagnosis,
          restDays,
          statement,
          observations,
        }),
      }, user);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo guardar el certificado');
      setCertificates((current) => [data.certificate, ...current]);
      setStatusMessage(`Certificado guardado. Codigo: ${data.certificate.verificationCode}. Usa el boton PDF en el historial para imprimir o descargar.`);
    } catch (error) {
      console.error('Error saving certificate:', error);
      setStatusMessage(error.message || 'No se pudo emitir el certificado.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="digital-prescription-panel">
      <div className="clinical-files-header">
        <div>
          <h3>Certificados medicos</h3>
          <p>Reposo, asistencia o condicion medica con firma y PDF verificable.</p>
        </div>
        <span>{certificates.length} cert.</span>
      </div>

      <div className="prescription-medication-card">
        <div className="prescription-grid">
          <select value={certificateType} onChange={(event) => setCertificateType(event.target.value)}>
            <option value="asistencia">Asistencia</option>
            <option value="reposo">Reposo medico</option>
            <option value="condicion">Condicion medica</option>
          </select>
          <input value={restDays} onChange={(event) => setRestDays(event.target.value)} type="number" min="0" placeholder="Dias de reposo" />
          <input value={diagnosis} onChange={(event) => setDiagnosis(event.target.value)} placeholder="Diagnostico si aplica" />
        </div>
        <textarea
          value={statement}
          onChange={(event) => setStatement(event.target.value)}
          placeholder="Texto principal del certificado. Si lo dejas vacio, el sistema genera uno segun el tipo."
          rows={3}
        />
        <textarea
          value={observations}
          onChange={(event) => setObservations(event.target.value)}
          placeholder="Observaciones, restricciones, recomendaciones o aclaraciones"
          rows={3}
        />
      </div>

      <button type="button" className="btn btn-primary full-width" onClick={saveCertificate} disabled={isSaving}>
        {isSaving ? 'Guardando...' : 'Guardar certificado'}
      </button>
      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}

      {certificates.length > 0 && (
        <div className="prescription-history">
          <strong>Certificados emitidos</strong>
          {certificates.slice(0, 4).map((certificate) => (
            <div key={certificate.id} className="prescription-history-item">
              <span>
                {new Date(certificate.createdAt).toLocaleString('es-EC')} · {certificateTypeLabels[certificate.certificateType] || 'Certificado'}
                {certificate.restDays ? ` · ${certificate.restDays} dia(s)` : ''}
              </span>
              <button type="button" onClick={() => openPdf(certificate)}>PDF</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MedicalCertificatesPanel;
